import { ExtractedSymptom } from '../components/llm-diagnose/LLMDiagnoseList';
import { gekkoApi, GekkoApiError } from '@gekkovet-oy/shared-components';

export type ExtractedAnimal = {
  animal_type_id: string;
  breed: string;
  date_of_birth: string | null;
  weight: string | null;
  name: string;
  gender: 'male' | 'female';
  neutered: boolean;
};

export type SymptomMatch = {
  _distance: number;
  id: number;
  symptom_name: string;
  category_name: string;
};

type SymptomWithKeyPhrases = {
  symptom: SymptomMatch;
  keyPhrases: string[];
};

type Case = {
  id: string;
  notes: string;
  status: string;
  created_at: string;
  updated_at: string;
  ai_extracted_patient?: ExtractedAnimal;
  suggested_symptoms: SymptomWithKeyPhrases[];
  sanitized_symptoms?: SymptomWithKeyPhrases[];
  error?: string;
};

const CERTAINTY_FACTOR = 0.35;

export class LLMError extends Error {
  httpStatus: number;
  constructor(message: string, httpStatus: number) {
    super(message);
    this.name = 'LLMError';
    this.httpStatus = httpStatus;
  }
}

export async function sanitizeSymptomResults(caseId: string): Promise<{
  symptoms: { [key: string]: ExtractedSymptom[] };
}> {
  try {
    let c = await gekkoApi.get<Case>(`/cases/${caseId}`);
    while (
      c.status === 'SANITIZING' ||
      c.status === 'INITIAL_AI_PROCESSING_DONE'
    ) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      c = await gekkoApi.get<Case>(`/cases/${caseId}`);
    }
    if (c.status === 'ERROR') {
      throw new LLMError(c.error || 'Unknown error', 500);
    }
    const sanitizedSymptoms = c.sanitized_symptoms || [];
    const obj: { [key: string]: ExtractedSymptom[] } = {};
    for (const symptom of sanitizedSymptoms) {
      if (!obj[symptom.symptom.category_name])
        obj[symptom.symptom.category_name] = [];
      // filter out duplicates
      if (
        obj[symptom.symptom.category_name].some(
          (s) => s.id === symptom.symptom.id
        )
      )
        continue;
      obj[symptom.symptom.category_name].push({
        id: symptom.symptom.id,
        name: symptom.symptom.symptom_name,
        category_name: symptom.symptom.category_name,
        reason: symptom.keyPhrases[0],
        uncertain: symptom.symptom._distance > CERTAINTY_FACTOR,
      });
    }
    return {
      symptoms: obj,
    };
  } catch (e) {
    if (e instanceof GekkoApiError) {
      throw new LLMError(e.message, e.httpStatus);
    }
    throw e;
  }
}

export async function extractSymptomsRequest({
  anamnesis,
  caseId,
}: {
  anamnesis: string;
  caseId?: string;
}): Promise<{
  symptoms: { [key: string]: ExtractedSymptom[] };
  animal: ExtractedAnimal;
  caseId: string;
}> {
  try {
    let c: Case = caseId
      ? await gekkoApi.put<Case, Pick<Case, 'notes'>>(`/cases/${caseId}`, {
          body: {
            notes: anamnesis,
          },
        })
      : await gekkoApi.post<Case>('/cases', {
          body: {
            notes: anamnesis,
          },
        });
    while (
      c.status === 'PROCESSING' ||
      c.status === 'PENDING' ||
      c.status === 'QUEUED'
    ) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      c = await gekkoApi.get<Case>(`/cases/${c.id}`);
    }
    if (c.status === 'ERROR') {
      throw new LLMError(c.error || 'Unknown error', 500);
    }

    const suggestedSymptoms = c.suggested_symptoms || [];

    const obj: { [key: string]: ExtractedSymptom[] } = {};
    for (const symptom of suggestedSymptoms) {
      if (!obj[symptom.symptom.category_name])
        obj[symptom.symptom.category_name] = [];
      // filter out duplicates
      if (
        obj[symptom.symptom.category_name].some(
          (s) => s.id === symptom.symptom.id
        )
      )
        continue;
      obj[symptom.symptom.category_name].push({
        id: symptom.symptom.id,
        name: symptom.symptom.symptom_name,
        category_name: symptom.symptom.category_name,
        reason: symptom.keyPhrases[0],
        uncertain: symptom.symptom._distance > CERTAINTY_FACTOR,
      });
    }
    return {
      symptoms: obj,
      animal: c.ai_extracted_patient || ({} as any),
      caseId: c.id,
    };
  } catch (e) {
    if (e instanceof GekkoApiError) {
      throw new LLMError(e.message, e.httpStatus);
    }
    throw e;
  }
}
